import React, { useState, useEffect, useRef } from 'react';
import {
  Paper,
  Typography,
  Box,
  TextField,
  IconButton,
  Avatar,
  List,
  ListItem,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  Send,
  Person,
  DirectionsCar,
  ArrowBack,
  AdminPanelSettings
} from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import { useSelector, useDispatch } from "react-redux";
import { api } from 'common';
import { useTranslation } from "react-i18next";
import { colors } from '../components/Theme/WebTheme';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';

const useStyles = makeStyles(theme => ({
  rootContainer: {
    padding: 20,
    height: '100vh',
    overflow: 'hidden'
  },
  paper: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  header: {
    padding: '16px 20px',
    borderBottom: '1px solid #e0e0e0',
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#f8f9fa'
  },
  backButton: {
    marginRight: 12
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flex: 1
  },
  userTypeChip: {
    fontSize: '0.7rem',
    height: 20
  },
  customerChip: {
    backgroundColor: '#4caf50',
    color: '#fff'
  },
  driverChip: {
    backgroundColor: '#ff9800',
    color: '#fff'
  },
  messagesContainer: {
    flex: 1,
    overflow: 'auto',
    padding: '16px 0',
    '&::-webkit-scrollbar': {
      width: 6,
    },
    '&::-webkit-scrollbar-track': {
      background: '#f1f1f1',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#888',
      borderRadius: 3,
    },
  },
  messagesList: {
    padding: '0 20px'
  },
  messageItem: {
    padding: '8px 0',
    border: 'none'
  },
  messageBubble: {
    maxWidth: '70%',
    padding: '12px 16px',
    borderRadius: 18,
    marginBottom: 4
  },
  userMessage: {
    backgroundColor: '#e3f2fd',
    marginLeft: 'auto',
    borderBottomRightRadius: 4
  },
  adminMessage: {
    backgroundColor: '#f5f5f5',
    marginRight: 'auto',
    borderBottomLeftRadius: 4
  },
  messageText: {
    margin: 0,
    wordWrap: 'break-word'
  },
  messageTime: {
    fontSize: '0.75rem',
    color: '#666',
    textAlign: 'right',
    marginTop: 4
  },
  messageInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    marginBottom: 8,
    fontSize: '0.75rem',
    color: '#666'
  },
  userMessageContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    marginBottom: 16
  },
  adminMessageContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginBottom: 16
  },
  inputContainer: {
    padding: '16px 20px',
    borderTop: '1px solid #e0e0e0',
    backgroundColor: '#fff'
  },
  inputField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 25,
    }
  },
  sendButton: {
    backgroundColor: colors.MAIN_COLOR,
    color: '#fff',
    marginLeft: 8,
    '&:hover': {
      backgroundColor: colors.MAIN_COLOR,
      opacity: 0.8
    }
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 200
  },
  emptyState: {
    textAlign: 'center',
    padding: 40,
    color: '#999'
  }
}));

export default function AdminSupportChat() {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userId } = useParams();

  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userData, setUserData] = useState(null);
  const messagesEndRef = useRef(null);

  const {
    fetchAdminChatMessages,
    sendAdminMessage,
    stopFetchAdminMessages,
    markAdminMessagesAsRead
  } = api;

  const auth = useSelector(state => state.auth);
  const adminChats = useSelector(state => state.adminsupportdata.messages);
  const loading = useSelector(state => state.adminsupportdata.loading);

  // Fetch user data for displaying name
  useEffect(() => {
    if (userId && api.fetchUsersOnce) {
      const fetchUserData = async () => {
        try {
          // Fetch all users and find the specific user
          dispatch(api.fetchUsersOnce()).then(() => {
            // This will be handled in the users selector
          });
        } catch (error) {
          console.log('Error fetching user data:', error);
          setUserData({
            firstName: 'User',
            lastName: '',
            profile_image: null
          });
        }
      };
      fetchUserData();
    }
  }, [userId, dispatch]);

  // Get user data from Redux store
  const users = useSelector(state => state.usersdata.users);

  useEffect(() => {
    if (users && Array.isArray(users) && userId) {
      const user = users.find(u => u.uid === userId);
      if (user) {
        setUserData({
          firstName: user.firstName || 'User',
          lastName: user.lastName || '',
          profile_image: user.profile_image || null
        });
      } else {
        setUserData({
          firstName: 'User',
          lastName: '',
          profile_image: null
        });
      }
    }
  }, [users, userId]);

  useEffect(() => {
    if (userId) {
      dispatch(fetchAdminChatMessages(userId));
      // Mark messages as read when admin opens the chat
      setTimeout(() => {
        dispatch(markAdminMessagesAsRead(userId, 'admin'));
      }, 1000); // Wait 1 second to ensure messages are loaded first
    }

    return () => {
      if (userId) {
        dispatch(stopFetchAdminMessages(userId));
      }
    };
  }, [userId, dispatch, fetchAdminChatMessages, markAdminMessagesAsRead, stopFetchAdminMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [adminChats]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSendMessage = async () => {
    if (!message.trim() || !userId || isSubmitting) return;

    const messageText = message.trim();
    setMessage('');
    setIsSubmitting(true);

    try {
      // Get user's push token for notification
      let userToken = null;
      if (users && Array.isArray(users)) {
        const user = users.find(u => u.uid === userId);
        if (user && user.pushToken) {
          userToken = user.pushToken;
          console.log('✅ Found user push token:', userToken);
        } else {
          console.log('⚠️ User found but no push token:', user);
        }
      } else {
        console.log('⚠️ No users data available for push token');
      }

      console.log('📤 Sending admin message with data:', {
        userId: userId,
        message: messageText,
        userType: userType,
        userToken: userToken,
        hasToken: !!userToken
      });

      await dispatch(sendAdminMessage({
        userId: userId,
        message: messageText,
        from: 'admin',
        fromType: 'admin',
        userType: userType, // Use the consistent userType from first user message
        userToken: userToken, // Pass the user's push token for notification
        adminTokens: [],
        adminName: auth.profile ? `${auth.profile.firstName || ''} ${auth.profile.lastName || ''}`.trim() || 'Admin' : 'Admin',
        adminImage: auth.profile?.profile_image || null
      }));
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (timestamp) => {
    return moment(timestamp).format('HH:mm');
  };

  const formatMessageDate = (timestamp) => {
    const messageDate = moment(timestamp);
    const today = moment();

    if (messageDate.isSame(today, 'day')) {
      return formatTime(timestamp);
    } else {
      return messageDate.format('DD/MM/YY HH:mm');
    }
  };

  // Get user type from first message and consistent user name
  const userType = adminChats.length > 0 ? adminChats[0].userType : 'customer';

  // Get consistent user name from the first user message (not admin message)
  const getUserNameFromMessages = () => {
    const userMessage = adminChats.find(msg => msg.fromType === 'user');
    if (userMessage && userMessage.userName) {
      return userMessage.userName;
    }
    return null;
  };

  // Display name - prioritize user name from messages, then userData, then fallback
  const displayName = getUserNameFromMessages() ||
    (userData ? `${userData.firstName} ${userData.lastName}`.trim() : null) ||
    (userId ? `${userId.slice(0, 8)}...` : t('unknown_user'));

  if (loading) {
    return (
      <div className={classes.rootContainer}>
        <Paper className={classes.paper}>
          <div className={classes.loadingContainer}>
            <CircularProgress />
          </div>
        </Paper>
      </div>
    );
  }

  return (
    <div className={classes.rootContainer}>
      <Paper className={classes.paper}>
        {/* Header */}
        <div className={classes.header}>
          <IconButton
            className={classes.backButton}
            onClick={() => navigate('/admin-support-list')}
          >
            <ArrowBack />
          </IconButton>

          <div className={classes.userInfo}>
            <Avatar src={userData?.profile_image}>
              {userType === 'customer' ? <Person /> : <DirectionsCar />}
            </Avatar>
            <Box>
              <Typography variant="h6">
                {displayName}
              </Typography>
              <Chip
                size="small"
                label={t(userType)}
                className={`${classes.userTypeChip} ${userType === 'customer' ? classes.customerChip : classes.driverChip}`}
              />
            </Box>
          </div>
        </div>

        {/* Messages */}
        <div className={classes.messagesContainer}>
          {adminChats.length === 0 ? (
            <div className={classes.emptyState}>
              <Typography variant="h6">
                {t('no_messages_yet')}
              </Typography>
              <Typography variant="body2">
                {t('start_conversation')}
              </Typography>
            </div>
          ) : (
            <List className={classes.messagesList}>
              {adminChats.map((chat, index) => (
                <ListItem key={chat.msgId || index} className={classes.messageItem}>
                  <Box width="100%">
                    {chat.fromType === 'admin' ? (
                      <div className={classes.adminMessageContainer}>
                        {/* Admin message info */}
                        <div className={classes.messageInfo}>
                          <AdminPanelSettings fontSize="inherit" />
                          <span>{chat.adminName || t('admin')}</span>
                        </div>
                        {/* Admin message bubble */}
                        <Box className={`${classes.messageBubble} ${classes.adminMessage}`}>
                          <Typography className={classes.messageText}>
                            {chat.message}
                          </Typography>
                          <div className={classes.messageTime}>
                            {formatMessageDate(chat.createdAt)}
                          </div>
                        </Box>
                      </div>
                    ) : (
                      <div className={classes.userMessageContainer}>
                        {/* User message info */}
                        <div className={classes.messageInfo}>
                          {userType === 'customer' ? <Person fontSize="inherit" /> : <DirectionsCar fontSize="inherit" />}
                          <span>{chat.userName || t(userType)}</span>
                        </div>
                        {/* User message bubble */}
                        <Box className={`${classes.messageBubble} ${classes.userMessage}`}>
                          <Typography className={classes.messageText}>
                            {chat.message}
                          </Typography>
                          <div className={classes.messageTime}>
                            {formatMessageDate(chat.createdAt)}
                          </div>
                        </Box>
                      </div>
                    )}
                  </Box>
                </ListItem>
              ))}
              <div ref={messagesEndRef} />
            </List>
          )}
        </div>

        {/* Input */}
        <div className={classes.inputContainer}>
          <Box display="flex" alignItems="flex-end">
            <TextField
              className={classes.inputField}
              fullWidth
              multiline
              maxRows={4}
              placeholder={t('type_message_user')}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              variant="outlined"
              size="small"
              disabled={isSubmitting}
            />
            <IconButton
              className={classes.sendButton}
              onClick={handleSendMessage}
              disabled={!message.trim() || isSubmitting}
            >
              <Send />
            </IconButton>
          </Box>
        </div>
      </Paper>
    </div>
  );
} 